import { useEffect, useRef } from 'react';

import { IS_DEV, isRunningInNativeApp, native } from '@lemoment/shared';

/**
 * Template stub: send the device push token to your backend so it can target
 * this device. Left unimplemented on purpose — the starter has no push
 * backend. Wire this to your API (e.g. `POST /users/0/register-device`), and
 * in a real app call it only once the user is authenticated.
 */
const registerPushToken = async (
  token: string,
  platform: 'ios' | 'android',
): Promise<void> => {
  // TODO: await apiClient.post('/users/0/register-device', {
  //   platform, deviceToken: token, application: 'lemoment', stage: ENV,
  // });
  if (IS_DEV) {
    console.info(
      '[push] device token ready (wire to backend):',
      platform,
      token,
    );
  }
};

/**
 * Request notification permission and fetch the push token on boot, then
 * register it with the backend. Re-registers when the token rotates.
 * No-op outside the native shell.
 */
export const usePushSetup = (): void => {
  const registeredTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isRunningInNativeApp()) {
      return;
    }

    const register = async (token: string, platform: 'ios' | 'android') => {
      if (registeredTokenRef.current === token) {
        return;
      }
      registeredTokenRef.current = token;
      await registerPushToken(token, platform);
    };

    const setup = async () => {
      try {
        const { status } = await native.invoke('requestPermission', {
          kind: 'notification',
        });
        if (status !== 'granted') {
          return;
        }
        const { token, platform } = await native.invoke('getPushToken');
        if (token) {
          await register(token, platform);
        }
      } catch (err) {
        console.warn('[push] setup failed:', err);
      }
    };

    void setup();

    // FCM may mint the token after our first getPushToken() (e.g. APNs
    // arrives late) — re-register when native pushes a rotation.
    return native.on('pushTokenChanged', ({ token, platform }) => {
      void register(token, platform);
    });
  }, []);
};
